import React, {
  type ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Drawer, Image, Modal } from "antd";
import ReactMarkdown from "react-markdown";
import {
  mainSwalDoAndSkip,
  swalError,
  swalSuccess,
} from "../../../functions/swalOptions";
import { urlAPIv2 } from "../../../urls";
import {
  deleteInsiderPost,
  getInsiderErrorMessage,
  InsiderApiError,
  type InsiderAdminPost,
  type InsiderReaction,
  listInsiderPosts,
  normalizeInsiderPostForm,
  publishInsiderPost,
  saveInsiderPost,
  serializeInsiderPostForm,
  trimmedString,
  unpublishInsiderPost,
  uploadInsiderMedia,
  validateInsiderPostForm,
} from "./insiderAdmin.api";
import { applyMarkdownCommand, type MarkdownCommand } from "./insiderMarkdown";
import {
  insiderPushRecipients,
  plainTextExcerpt,
  sendInsiderPush,
} from "./insiderPush";
import { useAppSelector } from "../../../hooks/redux";
import { type RootState } from "../../../store/store";
import { type User } from "../../../store/slices/userSlice";
import "./InsiderAdminPage.scss";
import {
  categoryOptionsForValue,
  isResizeObserverLoopMessage,
  progressPercentage,
} from "./insiderComposerUi";
import { insiderGalleryVariant } from "./insiderGallery";
import {
  detectImageMediaOrientation,
  detectVideoMediaOrientation,
  mediaOrientationAspectRatio,
  shouldApplyAutoMediaOrientation,
  type MediaOrientation,
} from "./insiderMediaOrientation";

const publicationTypes = [
  "text",
  "article",
  "image",
  "native-video",
  "youtube",
  "external-link",
  "announcement",
];
const publicationDescriptions: Record<string, string> = {
  text: "Text post — formatted text with an optional main image or GIF.",
  article: "Text post — formatted text with an optional main image or GIF.",
  image: "Image post — formatted text with one or more images/GIFs.",
  "native-video": "Video post — formatted text with an uploaded video.",
  youtube: "YouTube post — formatted text with a YouTube video.",
  "external-link": "External link — formatted text with a link preview.",
  announcement:
    "Announcement — formatted announcement with an optional image or GIF.",
};
type FormState = {
  Title: string;
  Category: string;
  Audience: string;
  PublicationType: string;
  Excerpt: string;
  Body: string;
  ExpiresAt: string;
  PinnedPriority: number;
  Cover?: string;
  Images: string[];
  Video?: string;
  VideoPoster?: string;
  VideoAlt: string;
  YoutubeVideoId: string;
  ExternalUrl: string;
  ExternalTitle: string;
  ExternalDescription: string;
  ExternalImage?: string;
  CtaLabel: string;
  CtaUrl: string;
  PreviewOnly: boolean;
  MediaOrientation: MediaOrientation;
};
type PublishPhase =
  "confirm" | "publishing" | "preparing" | "sending" | "completed" | "failed";
const blank = (): FormState => ({
  Title: "",
  Category: "BSO News",
  Audience: "Both",
  PublicationType: "text",
  Excerpt: "",
  Body: "",
  ExpiresAt: "",
  PinnedPriority: 0,
  Images: [],
  VideoAlt: "",
  YoutubeVideoId: "",
  ExternalUrl: "",
  ExternalTitle: "",
  ExternalDescription: "",
  CtaLabel: "",
  CtaUrl: "",
  PreviewOnly: false,
  MediaOrientation: "landscape",
});

const messageDate = (post: InsiderAdminPost): string =>
  post.publishedAt ?? post.createdAt ?? post.updatedAt ?? "";
const dayLabel = (value: string): string =>
  value
    ? new Intl.DateTimeFormat(undefined, {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
      }).format(new Date(value))
    : "Unscheduled";
const timeLabel = (value: string): string =>
  value
    ? new Intl.DateTimeFormat(undefined, {
        hour: "2-digit",
        minute: "2-digit",
        day: "2-digit",
        month: "short",
      }).format(new Date(value))
    : "Just now";
const mediaUrl = (media: any): string | undefined => {
  const value =
    typeof media === "string"
      ? media
      : (media?.url ??
        media?.data?.attributes?.url ??
        media?.attributes?.url ??
        media?.formats?.large?.url);
  if (typeof value !== "string") return undefined;
  try {
    return new URL(value, urlAPIv2).toString();
  } catch {
    return value;
  }
};

const mediaKey = (media: any, url: string): string => {
  const id =
    media?.id ??
    media?.documentId ??
    media?.data?.id ??
    media?.attributes?.id;
  return id == null ? url : String(id);
};

export default function InsiderAdminPage(): ReactElement {
  const [posts, setPosts] = useState<InsiderAdminPost[]>([]);
  const [editing, setEditing] = useState<InsiderAdminPost | null>(null);
  const [form, setForm] = useState<FormState>(blank());
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [pushPost, setPushPost] = useState<InsiderAdminPost | null>(null);
  const [sendPush, setSendPush] = useState(true);
  const [pushTitle, setPushTitle] = useState("");
  const [pushMessage, setPushMessage] = useState("");
  const [pushProgress, setPushProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);
  const [pushPhase, setPushPhase] = useState<PublishPhase>("confirm");
  const [pushResult, setPushResult] = useState<{
    ok: number;
    skip: number;
    error: number;
  } | null>(null);
  const [pushDelivery, setPushDelivery] = useState({
    ok: 0,
    skip: 0,
    error: 0,
  });
  const [pushError, setPushError] = useState<string | null>(null);
  const [activePushTitle, setActivePushTitle] = useState<string | null>(null);
  const [progressExpanded, setProgressExpanded] = useState(true);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const allowPushNavigationRef = useRef(false);
  const restoringPushHistoryRef = useRef(false);
  // State updates are asynchronous; keep a synchronous guard for double clicks.
  const publishRequestInFlightRef = useRef(false);
  const orientationManuallySelectedRef = useRef(false);
  const orientationDetectionRef = useRef(0);
  const allUsers = useAppSelector(
    (state: RootState) => state.user.allUsers as User[],
  );
  const usersLoading = useAppSelector((state: RootState) => state.user.loading);

  const reload = async (): Promise<void> => {
    setLoading(true);
    setLoadError(null);
    try {
      setPosts(await listInsiderPosts());
    } catch (error: any) {
      setLoadError(error.message ?? "Could not load Insider history.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void reload();
  }, []);
  useEffect(() => {
    if (!drawerOpen && pushPost == null) return;
    const suppressKnownBrowserResizeObserverLoop = (
      event: ErrorEvent,
    ): void => {
      if (!isResizeObserverLoopMessage(event.message)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    };
    window.addEventListener(
      "error",
      suppressKnownBrowserResizeObserverLoop,
      true,
    );
    return () =>
      window.removeEventListener(
        "error",
        suppressKnownBrowserResizeObserverLoop,
        true,
      );
  }, [drawerOpen, pushPost]);

  const orderedPosts = useMemo(
    () =>
      [...posts].sort(
        (a, b) =>
          new Date(messageDate(a)).getTime() -
          new Date(messageDate(b)).getTime(),
      ),
    [posts],
  );
  const pushDeliveryActive =
    pushPhase === "publishing" ||
    pushPhase === "preparing" ||
    pushPhase === "sending";
  const pushRemainingSeconds =
    pushProgress == null
      ? 0
      : Math.max(0, pushProgress.total - pushProgress.done);
  const pushRemainingLabel =
    pushRemainingSeconds >= 60
      ? `About ${Math.ceil(pushRemainingSeconds / 60)} min remaining`
      : `About ${pushRemainingSeconds}s remaining`;
  const dismissPushProgress = (): void => {
    if (pushDeliveryActive) return;
    setActivePushTitle(null);
    setPushProgress(null);
    setPushResult(null);
    setPushDelivery({ ok: 0, skip: 0, error: 0 });
    setPushError(null);
    setPushPhase("confirm");
    setProgressExpanded(true);
  };
  const confirmLeavingActivePush = async (leave: () => void): Promise<void> => {
    const result = await mainSwalDoAndSkip(
      "Push notification delivery is in progress",
      "Leaving this page will stop the remaining notifications. Please wait until delivery is complete.",
      "LEAVE ANYWAY",
      "STAY ON PAGE",
      "warning",
    );
    if (!result.isConfirmed) return;
    allowPushNavigationRef.current = true;
    leave();
  };
  useEffect(() => {
    if (!pushDeliveryActive) return;
    const onBeforeUnload = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
      event.returnValue = "";
    };
    const onDocumentClick = (event: MouseEvent): void => {
      if (
        allowPushNavigationRef.current ||
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      )
        return;
      const target = event.target instanceof Element ? event.target : null;
      const link = target?.closest("a[href]") as HTMLAnchorElement | null;
      const logout =
        target?.textContent?.trim().toLowerCase() === "logout" &&
        target?.closest(".app-shell__sidebar-card, #menu_page") != null;
      if (link == null && !logout) return;
      const href =
        link == null ? null : new URL(link.href, window.location.href);
      if (
        href != null &&
        (href.origin !== window.location.origin ||
          href.href === window.location.href)
      )
        return;
      event.preventDefault();
      event.stopImmediatePropagation();
      void confirmLeavingActivePush(() => {
        if (link != null) window.location.assign(link.href);
        else (target as HTMLElement).click();
      });
    };
    const onPopState = (): void => {
      if (allowPushNavigationRef.current) return;
      if (restoringPushHistoryRef.current) {
        restoringPushHistoryRef.current = false;
        return;
      }
      restoringPushHistoryRef.current = true;
      window.history.go(1);
      void confirmLeavingActivePush(() => window.history.back());
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    document.addEventListener("click", onDocumentClick, true);
    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      document.removeEventListener("click", onDocumentClick, true);
      window.removeEventListener("popstate", onPopState);
    };
  }, [pushDeliveryActive]);
  const patch = <K extends keyof FormState>(
    key: K,
    value: FormState[K],
  ): void => {
    setForm((current) => ({ ...current, [key]: value }));
    setFieldErrors((current) => {
      const next = { ...current };
      delete next[key];
      return next;
    });
  };
  const openComposer = (post?: InsiderAdminPost): void => {
    setEditing(post ?? null);
    setFieldErrors({});
    orientationManuallySelectedRef.current = false;
    orientationDetectionRef.current += 1;
    setForm(
      post == null
        ? blank()
        : { ...blank(), ...post, ...normalizeInsiderPostForm(post) },
    );
    setDrawerOpen(true);
  };
  const validate = (): boolean => {
    const errors = validateInsiderPostForm(form);
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };
  const mapApiErrors = (error: unknown): boolean => {
    if (
      !(error instanceof InsiderApiError) ||
      !Array.isArray(error.details?.errors)
    )
      return false;
    const errors = error.details.errors.reduce(
      (all: Record<string, string>, item: any) => {
        const field = item.path?.at(-1) ?? item.field;
        const message =
          trimmedString(item.message) && item.message !== "i18next error"
            ? trimmedString(item.message)
            : getInsiderErrorMessage(error);
        return typeof field === "string" ? { ...all, [field]: message } : all;
      },
      {},
    );
    setFieldErrors(errors);
    return Object.keys(errors).length > 0;
  };
  const save = async (): Promise<void> => {
    if (!validate()) return;
    const serialized = serializeInsiderPostForm(form);
    if (Object.keys(serialized.errors).length > 0) {
      setFieldErrors(serialized.errors);
      return;
    }
    try {
      setBusy(true);
      await saveInsiderPost(editing?.id ?? null, serialized.data);
      await swalSuccess(
        editing == null ? "Insider draft saved." : "Insider post updated.",
      );
      setDrawerOpen(false);
      await reload();
    } catch (error: unknown) {
      if (!mapApiErrors(error)) void swalError(getInsiderErrorMessage(error));
    } finally {
      setBusy(false);
    }
  };
  const upload = async (
    key: "Cover" | "Images" | "Video" | "VideoPoster" | "ExternalImage",
    files: FileList | null,
  ): Promise<void> => {
    const selected = Array.from(files ?? []);
    if (selected.length === 0) return;
    if (key === "Images" && form.Images.length + selected.length > 10) {
      void swalError("An image post can contain up to 10 images or GIFs.");
      return;
    }
    const detectsPrimaryImage = key === "Images" && form.Images.length === 0;
    const detectsPrimaryVideo = key === "Video";
    if (detectsPrimaryImage || detectsPrimaryVideo) {
      orientationManuallySelectedRef.current = false;
      const detectionId = ++orientationDetectionRef.current;
      const detect = detectsPrimaryImage
        ? detectImageMediaOrientation(selected[0])
        : detectVideoMediaOrientation(selected[0]);
      void detect.then((orientation) => {
        if (
          detectionId === orientationDetectionRef.current &&
          shouldApplyAutoMediaOrientation(orientationManuallySelectedRef.current)
        )
          patch("MediaOrientation", orientation);
      });
    }
    try {
      setUploading(key);
      const uploads = await Promise.all(
        selected.map(
          async (file) =>
            await uploadInsiderMedia(file, editing?.id ?? null, key),
        ),
      );
      if (key === "Images")
        patch("Images", [...form.Images, ...uploads.map((item) => item.url)]);
      else patch(key, uploads[0].url);
    } catch (error: any) {
      void swalError(error.message ?? "Media upload failed.");
    } finally {
      setUploading(null);
    }
  };
  const changePublication = async (post: InsiderAdminPost): Promise<void> => {
    if (pushDeliveryActive) {
      void swalError("A BSO Insider push delivery is already in progress.");
      return;
    }
    if (post.publishedAt != null) {
      try {
        setBusy(true);
        await unpublishInsiderPost(post.id);
        await reload();
      } catch (error: any) {
        void swalError(error.message ?? "Could not update publication status.");
      } finally {
        setBusy(false);
      }
      return;
    }
    setPushPost(post);
    setSendPush(post.PreviewOnly !== true);
    setPushTitle(trimmedString(post.Title));
    setPushMessage(trimmedString(post.Excerpt) || plainTextExcerpt(post.Body));
    setPushProgress(null);
    setPushPhase("confirm");
    setPushResult(null);
    setPushDelivery({ ok: 0, skip: 0, error: 0 });
    setPushError(null);
  };
  const closePublishModal = (): void => {
    if (busy) return;
    setPushPost(null);
    setSendPush(true);
    setPushTitle("");
    setPushMessage("");
    setPushProgress(null);
    setPushPhase("confirm");
    setPushResult(null);
    setPushDelivery({ ok: 0, skip: 0, error: 0 });
    setPushError(null);
  };
  const saveActionLabel = editing == null ? "SAVE DRAFT" : "SAVE CHANGES";
  const pushAudienceLabel =
    pushPost?.Audience === "Agent"
      ? "Agent"
      : pushPost?.Audience === "Landlord"
        ? "Landlord / Client"
        : "Agent and Landlord / Client";
  const pushRecipients = useMemo(
    () =>
      pushPost == null
        ? []
        : insiderPushRecipients(allUsers, pushPost.Audience),
    [allUsers, pushPost],
  );
  const usersReadyForPush = !usersLoading && allUsers.length > 0;
  const publishingPreviewPost = pushPost?.PreviewOnly === true;
  const shouldSendPush = !publishingPreviewPost && sendPush;
  const publishPhaseLabel =
    pushPhase === "publishing"
      ? "Publishing post…"
      : pushPhase === "preparing"
        ? "Preparing recipients…"
        : pushPhase === "sending"
          ? "Sending push notifications…"
          : pushPhase === "completed"
            ? "Completed"
            : pushPhase === "failed"
              ? "Publishing failed"
              : "";
  const publishPrimaryLabel =
    pushPhase === "completed"
      ? "DONE"
      : busy
        ? publishPhaseLabel.toUpperCase()
        : publishingPreviewPost
          ? "PUBLISH DEVELOPER PREVIEW"
          : shouldSendPush
            ? "PUBLISH FOR EVERYONE & SEND PUSH"
            : "PUBLISH FOR EVERYONE";
  const progressPercent =
    pushProgress == null
      ? 0
      : progressPercentage(pushProgress.done, pushProgress.total);
  const confirmPublish = async (): Promise<void> => {
    if (
      pushPost == null ||
      busy ||
      pushDeliveryActive ||
      publishRequestInFlightRef.current
    )
      return;
    if (pushPhase === "completed") {
      closePublishModal();
      return;
    }
    if (shouldSendPush && !usersReadyForPush) {
      setPushError(
        "Recipients are still loading. Wait for the complete Admin users list before sending push notifications.",
      );
      return;
    }
    let published = false;
    setPushError(null);
    setPushResult(null);
    setPushDelivery({ ok: 0, skip: 0, error: 0 });
    publishRequestInFlightRef.current = true;
    setBusy(true);
    setPushPhase("publishing");
    try {
      await publishInsiderPost(pushPost.id, pushPost.PreviewOnly === true);
      published = true;
      await reload();
      if (!shouldSendPush) {
        setPushPhase("completed");
        return;
      }
      setActivePushTitle(trimmedString(pushPost.Title) || "BSO Insider post");
      setProgressExpanded(true);
      setPushPost(null);
      setPushPhase("preparing");
      setPushProgress({ done: 0, total: pushRecipients.length });
      setPushPhase("sending");
      const result = await sendInsiderPush(
        allUsers,
        pushPost.Audience,
        trimmedString(pushTitle),
        trimmedString(pushMessage),
        (done, total, sendResult) => {
          setPushProgress({ done, total });
          setPushDelivery((current) => ({
            ...current,
            [sendResult]: current[sendResult] + 1,
          }));
        },
      );
      setPushResult(result);
      setPushProgress({ done: result.recipients, total: result.recipients });
      setPushPhase("completed");
    } catch (error: any) {
      setPushError(
        published
          ? `Post published, but push was not sent: ${error.message ?? "unknown error"}`
          : (error.message ?? "Could not publish Insider post."),
      );
      setPushPhase(published ? "completed" : "failed");
    } finally {
      setBusy(false);
      publishRequestInFlightRef.current = false;
    }
  };
  const remove = async (post: InsiderAdminPost): Promise<void> => {
    const result = await mainSwalDoAndSkip(
      "Delete BSO Insider post?",
      "This post and its reactions will be permanently deleted.",
      "DELETE POST",
      "CANCEL",
      "warning",
    );
    if (!result.isConfirmed) return;
    try {
      setBusy(true);
      await deleteInsiderPost(post.id);
      await reload();
    } catch (error: any) {
      void swalError(error.message ?? "Could not delete Insider post.");
    } finally {
      setBusy(false);
    }
  };
  const insertMarkdown = (command: MarkdownCommand): void => {
    const textarea = bodyRef.current;
    const start = textarea?.selectionStart ?? trimmedString(form.Body).length;
    const end = textarea?.selectionEnd ?? start;
    const edit = applyMarkdownCommand(
      trimmedString(form.Body),
      start,
      end,
      command,
    );
    patch("Body", edit.value);
    requestAnimationFrame(() => {
      textarea?.focus();
      textarea?.setSelectionRange(edit.selectionStart, edit.selectionEnd);
    });
  };

  return (
    <div className="container-xxl py-8">
      <div className="card shadow-sm overflow-hidden">
        <div className="card-header border-0 d-flex align-items-center justify-content-between py-5">
          <div className="d-flex align-items-center gap-3">
            <div className="symbol symbol-50px">
              <div className="symbol-label bg-primary text-white fs-2 fw-bold">
                B
              </div>
            </div>
            <div>
              <h1 className="fs-2 fw-bold mb-0">BSO Insider</h1>
              <span className="text-muted">Official one-way channel</span>
            </div>
          </div>
          <button className="btn btn-primary" onClick={() => openComposer()}>
            CREATE POST
          </button>
        </div>
        <div
          className="card-body bg-light p-4 p-md-6"
          style={{ minHeight: 420 }}
        >
          {loading ? (
            <div className="d-flex justify-content-center py-12">
              <span
                className="spinner-border text-primary"
                role="status"
                aria-label="Loading Insider history"
              />
            </div>
          ) : loadError != null ? (
            <div className="text-center py-12">
              <p className="text-danger mb-4">{loadError}</p>
              <button className="btn btn-light" onClick={() => void reload()}>
                TRY AGAIN
              </button>
            </div>
          ) : orderedPosts.length === 0 ? (
            <div className="text-center py-12 text-muted">
              <i className="ki-duotone ki-message-text-2 fs-4x d-block mb-4" />
              <p className="fs-4 mb-1">No Insider posts yet</p>
              <p>Create the first official channel update.</p>
            </div>
          ) : (
            <div className="mx-auto" style={{ maxWidth: 820 }}>
              {orderedPosts.map((post, index) => (
                <React.Fragment key={post.id}>
                  {(index === 0 ||
                    dayLabel(messageDate(post)) !==
                      dayLabel(messageDate(orderedPosts[index - 1]))) && (
                    <div className="text-center my-5">
                      <span className="badge badge-light-primary">
                        {dayLabel(messageDate(post))}
                      </span>
                    </div>
                  )}
                  <PostBubble
                    post={post}
                    busy={busy}
                    pushActive={pushDeliveryActive}
                    onEdit={() => openComposer(post)}
                    onPublish={() => void changePublication(post)}
                    onDelete={() => void remove(post)}
                  />
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      </div>
      {activePushTitle != null && (
        <aside
          className="insider-push-progress-panel shadow-sm"
          aria-live="polite"
        >
          <div className="d-flex align-items-center justify-content-between gap-3">
            <div>
              <div className="fw-bold">Sending BSO Insider push</div>
              <div
                className="text-muted fs-8 text-truncate"
                style={{ maxWidth: 280 }}
              >
                {activePushTitle}
              </div>
            </div>
            <button
              type="button"
              className="btn btn-light btn-sm"
              onClick={() => setProgressExpanded((value) => !value)}
            >
              {progressExpanded ? "MINIMIZE" : "EXPAND"}
            </button>
          </div>
          {progressExpanded && (
            <div className="mt-4">
              {pushProgress != null && (
                <>
                  <div className="progress">
                    <div
                      className="progress-bar"
                      role="progressbar"
                      style={{ width: `${progressPercent}%` }}
                      aria-valuenow={pushProgress.done}
                      aria-valuemin={0}
                      aria-valuemax={pushProgress.total}
                    />
                  </div>
                  <div className="d-flex justify-content-between text-muted fs-8 mt-2">
                    <span>
                      {pushProgress.done} / {pushProgress.total} sent
                    </span>
                    <span>
                      {pushDeliveryActive ? pushRemainingLabel : "Completed"}
                    </span>
                  </div>
                </>
              )}
              <div className="text-muted fs-8 mt-2">
                Skipped: {(pushResult ?? pushDelivery).skip}; failed:{" "}
                {(pushResult ?? pushDelivery).error}.
              </div>
              {pushError != null && (
                <div className="text-danger fs-8 mt-2">{pushError}</div>
              )}
              {!pushDeliveryActive && (
                <div className="d-flex justify-content-end gap-2 mt-4">
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={dismissPushProgress}
                  >
                    DONE
                  </button>
                  <button
                    type="button"
                    className="btn btn-light btn-sm"
                    onClick={dismissPushProgress}
                  >
                    DISMISS
                  </button>
                </div>
              )}
            </div>
          )}
        </aside>
      )}
      <Drawer
        title={
          editing == null ? "Create BSO Insider post" : "Edit BSO Insider post"
        }
        placement="right"
        width={window.innerWidth <= 768 ? "100vw" : 680}
        rootClassName="insider-composer-drawer"
        open={drawerOpen}
        onClose={() => !busy && setDrawerOpen(false)}
        destroyOnClose
      >
        <div className="insider-composer">
          <div className="insider-composer__body">
            <div className="row g-4">
              <TextField
                label="Title"
                value={form.Title}
                error={fieldErrors.Title}
                onChange={(value) => patch("Title", value)}
              />
              <SelectField
                label="Category"
                value={form.Category}
                options={categoryOptionsForValue(form.Category)}
                error={fieldErrors.Category}
                onChange={(value) => patch("Category", value)}
              />
              <SelectField
                label="Audience"
                value={form.Audience}
                options={["Agent", "Landlord", "Both"]}
                error={fieldErrors.Audience}
                onChange={(value) => patch("Audience", value)}
              />
              <SelectField
                label="Publication type"
                value={form.PublicationType}
                options={publicationTypes}
                error={fieldErrors.PublicationType}
                onChange={(value) => patch("PublicationType", value)}
              />
              <TextField
                label="Excerpt"
                value={form.Excerpt}
                error={fieldErrors.Excerpt}
                onChange={(value) => patch("Excerpt", value)}
              />
              <TextField
                label="Expires at"
                type="datetime-local"
                value={form.ExpiresAt}
                onChange={(value) => patch("ExpiresAt", value)}
              />
              <TextField
                label="Pinned priority"
                type="number"
                value={String(form.PinnedPriority)}
                onChange={(value) => patch("PinnedPriority", Number(value))}
              />
              <div className="col-12">
                <label className="form-check form-switch form-check-custom form-check-solid">
                  <input className="form-check-input" type="checkbox" checked={form.PreviewOnly} onChange={(event) => patch("PreviewOnly", event.target.checked)} />
                  <span className="form-check-label ms-3">
                    <span className="fw-semibold d-block">Developer preview only</span>
                    <span className="text-muted fs-8">Publishes this post only to the configured developer audience. Regular users do not see it and no public unread or push is created. Use it to check content, media, CTA and preview reactions; publish for everyone is a separate action.</span>
                  </span>
                </label>
              </div>
              <div className="col-12">
                <p className="text-muted fs-8 mb-0">
                  {publicationDescriptions[form.PublicationType] ??
                    "Formatted text with optional media."}
                </p>
                <p className="text-muted fs-8 mb-0 mt-2">
                  Readers can react with Like, Love, Fire, Insightful, or
                  Unsure. Reactions are not configurable per post.
                </p>
              </div>
              <div className="col-12">
                <label className="form-label">Body (Markdown)</label>
                <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                  <span className="text-muted fs-8 me-2">
                    Select text, then choose a formatting action.
                  </span>
                  {(
                    [
                      ["heading", "H2", "Heading"],
                      ["bold", "Bold", "Bold"],
                      ["italic", "Italic", "Italic"],
                      ["quote", "Quote", "Quote"],
                      ["unordered-list", "List", "Bulleted list"],
                      ["ordered-list", "Ordered", "Numbered list"],
                      ["link", "Link", "Link"],
                    ] as Array<[MarkdownCommand, string, string]>
                  ).map(([command, label, title]) => (
                    <button
                      type="button"
                      className="btn btn-light btn-sm"
                      key={command}
                      title={title}
                      aria-label={title}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => insertMarkdown(command)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <textarea
                  ref={bodyRef}
                  className={`form-control ${fieldErrors.Body != null ? "is-invalid" : ""}`}
                  rows={8}
                  value={form.Body}
                  onChange={(event) => patch("Body", event.target.value)}
                  onKeyDown={(event) => {
                    if (
                      (event.ctrlKey || event.metaKey) &&
                      (event.key.toLowerCase() === "b" ||
                        event.key.toLowerCase() === "i")
                    ) {
                      event.preventDefault();
                      insertMarkdown(
                        event.key.toLowerCase() === "b" ? "bold" : "italic",
                      );
                    }
                  }}
                />
                <ErrorText error={fieldErrors.Body} />
                <div className="border rounded p-4 mt-3 bg-light">
                  <span className="text-muted fs-8 d-block mb-2">PREVIEW</span>
                  <ComposerPreview form={form} />
                </div>
              </div>
              <>
                {["text", "article", "announcement"].includes(
                  form.PublicationType,
                ) && (
                  <Media
                    label="Main post image (optional)"
                    description="Displayed as the main image inside the Insider message."
                    accept="image/*"
                    value={form.Cover}
                    uploading={uploading === "Cover"}
                    onUpload={(files) => void upload("Cover", files)}
                    onRemove={() => patch("Cover", undefined)}
                  />
                )}
                {form.PublicationType === "image" && (
                  <>
                    <Media
                      label="Post images"
                      description="The first image is the main media used for orientation."
                      accept="image/*"
                      multiple
                      value={form.Images}
                      uploading={uploading === "Images"}
                      onUpload={(files) => void upload("Images", files)}
                      onRemove={(url) =>
                        patch(
                          "Images",
                          form.Images.filter((item) => item !== url),
                        )
                      }
                    />
                    <MediaOrientationControl
                      value={form.MediaOrientation}
                      onChange={(orientation) => {
                        orientationManuallySelectedRef.current = true;
                        patch("MediaOrientation", orientation);
                      }}
                    />
                  </>
                )}
                {form.PublicationType === "native-video" && (
                  <>
                    <Media
                      label="Video"
                      description="The main media used for orientation."
                      accept="video/*"
                      value={form.Video}
                      uploading={uploading === "Video"}
                      onUpload={(files) => void upload("Video", files)}
                      onRemove={() => patch("Video", undefined)}
                    />
                    <MediaOrientationControl
                      value={form.MediaOrientation}
                      onChange={(orientation) => {
                        orientationManuallySelectedRef.current = true;
                        patch("MediaOrientation", orientation);
                      }}
                    />
                    <Media
                      label="Video poster"
                      description="Image shown before the video starts."
                      accept="image/*"
                      value={form.VideoPoster}
                      uploading={uploading === "VideoPoster"}
                      onUpload={(files) => void upload("VideoPoster", files)}
                      onRemove={() => patch("VideoPoster", undefined)}
                    />
                    <TextField
                      label="Video alternative text"
                      value={form.VideoAlt}
                      onChange={(value) => patch("VideoAlt", value)}
                    />
                  </>
                )}
                {form.PublicationType === "youtube" && (
                  <TextField
                    label="YouTube video ID"
                    value={form.YoutubeVideoId}
                    error={fieldErrors.YoutubeVideoId}
                    onChange={(value) => patch("YoutubeVideoId", value)}
                  />
                )}
                {form.PublicationType === "external-link" && (
                  <>
                    <TextField
                      label="External URL"
                      value={form.ExternalUrl}
                      error={fieldErrors.ExternalUrl}
                      onChange={(value) => patch("ExternalUrl", value)}
                    />
                    <TextField
                      label="External title"
                      value={form.ExternalTitle}
                      onChange={(value) => patch("ExternalTitle", value)}
                    />
                    <TextField
                      label="External description"
                      value={form.ExternalDescription}
                      onChange={(value) => patch("ExternalDescription", value)}
                    />
                    <Media
                      label="Link preview image (optional)"
                      description="Thumbnail displayed in the external link preview."
                      accept="image/*"
                      value={form.ExternalImage}
                      uploading={uploading === "ExternalImage"}
                      onUpload={(files) => void upload("ExternalImage", files)}
                      onRemove={() => patch("ExternalImage", undefined)}
                    />
                  </>
                )}
              </>
              <div className="col-12 border rounded p-4">
                <label className="form-label fw-bold">
                  Optional action button
                </label>
                <p className="text-muted fs-8">
                  Adds a button below the post. Use an internal route or HTTPS
                  URL.
                </p>
                <div className="row g-4">
                  <TextField
                    label="CTA label"
                    value={form.CtaLabel}
                    error={fieldErrors.CtaLabel}
                    onChange={(value) => patch("CtaLabel", value)}
                  />
                  <TextField
                    label="CTA URL"
                    value={form.CtaUrl}
                    error={fieldErrors.CtaUrl}
                    onChange={(value) => patch("CtaUrl", value)}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="insider-composer__footer d-flex justify-content-end gap-3">
            <button
              className="btn btn-light"
              disabled={busy}
              onClick={() => setDrawerOpen(false)}
            >
              CANCEL
            </button>
            <button
              className="btn btn-primary"
              disabled={busy || uploading != null}
              onClick={() => void save()}
            >
              {busy ? "SAVING…" : saveActionLabel}
            </button>
          </div>
        </div>
      </Drawer>
      <Modal
        title={publishingPreviewPost ? "Publish developer preview" : "Publish for everyone"}
        open={pushPost != null}
        width={540}
        centered
        className="insider-publish-modal"
        wrapClassName="insider-publish-modal-wrap"
        maskClosable={!busy}
        keyboard={!busy}
        onCancel={closePublishModal}
        footer={
          <div className="insider-publish-modal__footer d-flex justify-content-end gap-3">
            <button
              type="button"
              className="btn btn-light"
              disabled={busy}
              onClick={closePublishModal}
            >
              CANCEL
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy}
              onClick={() => void confirmPublish()}
            >
              {publishPrimaryLabel}
            </button>
          </div>
        }
      >
        <div className="insider-publish-modal__body">
          <p className="text-muted mb-3">
            {publishingPreviewPost
              ? "Publish this post to the configured developer audience only. Regular users will not see it, and no public unread or push is created."
              : "Publish this post for regular users. Push notifications are sent only after publication succeeds."}
          </p>
          <div className="insider-publish-modal__info text-muted fs-8">
            <span>
              Audience:{" "}
              <strong className="text-dark">{pushAudienceLabel}</strong>
            </span>
            {shouldSendPush && (
              <span>
                Eligible recipients:{" "}
                <strong className="text-dark">
                  {usersReadyForPush
                    ? pushRecipients.length
                    : "Loading complete users list…"}
                </strong>
              </span>
            )}
          </div>
          {publishingPreviewPost ? (
            <p className="text-muted fs-8 mb-4">
              Check the content, media, CTA and preview reactions in the developer audience. A public release requires a separate publish action.
            </p>
          ) : pushPhase !== "completed" && (
            <div className="form-check form-switch mb-4">
              <input
                id="insider-send-push"
                className="form-check-input"
                type="checkbox"
                checked={sendPush}
                onChange={(event) => setSendPush(event.target.checked)}
                disabled={busy}
              />
              <label className="form-check-label" htmlFor="insider-send-push">
                Send push notification
              </label>
            </div>
          )}
          {shouldSendPush && pushPhase !== "completed" && (
            <>
              <label className="form-label">Push title</label>
              <input
                className="form-control mb-3"
                value={pushTitle}
                onChange={(event) => setPushTitle(event.target.value)}
                disabled={busy}
              />
              <label className="form-label">Push message</label>
              <textarea
                className="form-control"
                rows={3}
                value={pushMessage}
                onChange={(event) => setPushMessage(event.target.value)}
                disabled={busy}
              />
            </>
          )}
          {pushError != null && (
            <div className="alert alert-danger mt-4 mb-0">{pushError}</div>
          )}
          {pushPhase !== "confirm" && (
            <div className="insider-publish-progress mt-4">
              <div className="fw-semibold mb-2">{publishPhaseLabel}</div>
              {pushProgress != null && (
                <>
                  <div className="progress">
                    <div
                      className="progress-bar"
                      role="progressbar"
                      style={{ width: `${progressPercent}%` }}
                      aria-valuenow={pushProgress.done}
                      aria-valuemin={0}
                      aria-valuemax={pushProgress.total}
                    />
                  </div>
                  <div className="text-muted fs-8 mt-2">
                    {pushProgress.done} / {pushProgress.total} sent
                  </div>
                  <div className="text-muted fs-8 mt-2">
                    Sent: {(pushResult ?? pushDelivery).ok}; skipped:{" "}
                    {(pushResult ?? pushDelivery).skip}; failed:{" "}
                    {(pushResult ?? pushDelivery).error}.
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}

function PostBubble({
  post,
  busy,
  pushActive,
  onEdit,
  onPublish,
  onDelete,
}: {
  post: InsiderAdminPost;
  busy: boolean;
  pushActive: boolean;
  onEdit: () => void;
  onPublish: () => void;
  onDelete: () => void;
}): ReactElement {
  const [expanded, setExpanded] = useState(false);
  const imageItems = [post.Cover, ...(post.Images ?? []), post.ExternalImage]
    .map((media) => {
      const url = mediaUrl(media);
      return url == null ? null : { media, url };
    })
    .filter(
      (item): item is { media: any; url: string } => item != null,
    );
  const video = mediaUrl(post.Video);
  return (
    <div className="d-flex gap-3 mb-5">
      <div className="symbol symbol-40px flex-shrink-0">
        <div className="symbol-label bg-primary text-white fw-bold">B</div>
      </div>
      <div className="flex-grow-1">
        <div className="bg-white rounded shadow-sm p-4">
          <div className="d-flex justify-content-between gap-3">
            <strong>{post.Title}</strong>
            <span
              className={`badge ${post.publishedAt == null ? "badge-light-warning" : post.PreviewOnly === true ? "badge-light-info" : "badge-light-success"}`}
            >
              {post.publishedAt == null
                ? "Draft"
                : post.PreviewOnly === true
                  ? "Developer preview"
                  : "Published"}
            </span>
          </div>
          <div className="text-muted fs-8 mt-1">
            {post.Category} · {post.Audience} · {post.PublicationType}
          </div>
          {!expanded && (post.Excerpt || plainTextExcerpt(post.Body)) && (
            <p className="mt-3 mb-0 text-muted insider-post-summary">
              {post.Excerpt || plainTextExcerpt(post.Body)}
            </p>
          )}
          {expanded && (
            <>
              {post.Excerpt && (
                <p className="mt-3 mb-2 fw-semibold">{post.Excerpt}</p>
              )}
              <MarkdownContent value={post.Body} />
              {imageItems.length > 0 && (
                <div className="insider-admin-post-media">
                  <Image.PreviewGroup>
                    <div
                      className={`insider-post-gallery insider-post-gallery--${insiderGalleryVariant(imageItems.length)} mt-2`}
                    >
                      {imageItems.map(({ media, url }) => (
                        <Image
                          className="insider-post-gallery__item"
                          key={mediaKey(media, url)}
                          src={url}
                          alt="Insider attachment"
                        />
                      ))}
                    </div>
                  </Image.PreviewGroup>
                </div>
              )}
              {video != null && (
                <div className="insider-admin-post-media">
                  <video
                    className="w-100 rounded mt-2"
                    controls
                    poster={mediaUrl(post.VideoPoster)}
                  >
                    <source src={video} />
                  </video>
                </div>
              )}
              {post.PublicationType === "youtube" && post.YoutubeVideoId && (
                <div className="mt-2 text-muted">
                  YouTube: {post.YoutubeVideoId}
                </div>
              )}
              {post.ExternalUrl && (
                <a
                  className="d-block mt-2"
                  href={post.ExternalUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  {post.ExternalTitle || post.ExternalUrl}
                </a>
              )}
              {post.CtaUrl && (
                <a
                  className="btn btn-sm btn-primary mt-3"
                  href={post.CtaUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  {post.CtaLabel || "Open link"}
                </a>
              )}
              <ReactionSummary post={post} />
            </>
          )}
          <div className="d-flex justify-content-between align-items-center mt-4">
            <span className="text-muted fs-8">
              {timeLabel(messageDate(post))}
            </span>
            <div className="d-flex gap-2">
              <button
                className="btn btn-light btn-sm"
                aria-expanded={expanded}
                onClick={() => setExpanded((value) => !value)}
              >
                {expanded ? "Hide details" : "Show details"}
              </button>
              <button
                className="btn btn-light btn-sm"
                disabled={busy}
                onClick={onEdit}
              >
                Edit
              </button>
              <button
                className="btn btn-primary btn-sm"
                disabled={busy}
                onClick={onPublish}
              >
                {pushActive
                  ? "Sending push…"
                  : post.publishedAt == null
                    ? "Publish"
                    : "Unpublish"}
              </button>
              <button
                className="btn btn-light-danger btn-sm"
                disabled={busy}
                onClick={onDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
const reactionEmoji: Record<InsiderReaction, string> = {
  like: "👍",
  love: "❤️",
  fire: "🔥",
  insightful: "💡",
  unsure: "🤔",
};
function ReactionSummary({
  post,
}: {
  post: InsiderAdminPost;
}): ReactElement | null {
  const reactions = post.engagement?.reactions;
  if (reactions == null) return null;
  const visible = (Object.keys(reactionEmoji) as InsiderReaction[]).filter(
    (reaction) => Number(reactions[reaction]) > 0,
  );
  return (
    <div
      className="d-flex align-items-center gap-2 mt-3 text-muted fs-8"
      aria-label={`Reactions: ${post.engagement?.totalReactions ?? 0}`}
    >
      {visible.map((reaction) => (
        <span className="badge badge-light" key={reaction}>
          {reactionEmoji[reaction]} {reactions[reaction]}
        </span>
      ))}
      <span>{post.engagement?.totalReactions ?? 0} reactions</span>
    </div>
  );
}
const markdownComponents = {
  blockquote: ({ children }: any) => (
    <blockquote
      style={{
        borderLeft: "3px solid var(--bs-gray-400)",
        background: "var(--bs-gray-100)",
        padding: "0.75rem 1rem",
        margin: "0.75rem 0",
        borderRadius: 4,
      }}
    >
      {children}
    </blockquote>
  ),
  strong: ({ children }: any) => (
    <strong className="fw-bold">{children}</strong>
  ),
  em: ({ children }: any) => <em>{children}</em>,
  h1: ({ children }: any) => <h1 className="fs-2 mt-4 mb-2">{children}</h1>,
  h2: ({ children }: any) => <h2 className="fs-3 mt-4 mb-2">{children}</h2>,
  h3: ({ children }: any) => <h3 className="fs-4 mt-3 mb-2">{children}</h3>,
  p: ({ children }: any) => (
    <p style={{ overflowWrap: "anywhere" }}>{children}</p>
  ),
  a: ({ children, href }: any) => (
    <a
      className="link-primary"
      style={{ overflowWrap: "anywhere" }}
      href={href}
      target="_blank"
      rel="noreferrer"
    >
      {children}
    </a>
  ),
};
function MarkdownContent({ value }: { value: unknown }): ReactElement {
  return (
    <div className="insider-markdown">
      <ReactMarkdown components={markdownComponents}>
        {trimmedString(value)}
      </ReactMarkdown>
    </div>
  );
}
function ComposerPreview({ form }: { form: FormState }): ReactElement {
  const mainImage = form.PublicationType === "image" ? form.Images[0] : undefined;
  const additionalImages = form.PublicationType === "image" ? form.Images.slice(1) : [];
  const hasOrientedMedia =
    (form.PublicationType === "image" && mainImage != null) ||
    (form.PublicationType === "native-video" && form.Video != null);
  const orientationLabel =
    form.MediaOrientation === "portrait" ? "Portrait 9:16" : "Landscape 16:9";
  return (
    <div>
      <MarkdownContent value={form.Body} />
      {hasOrientedMedia && (
        <>
          <div
            className="insider-composer-media-preview mt-2"
            style={{ aspectRatio: mediaOrientationAspectRatio(form.MediaOrientation) }}
          >
            {mainImage != null && (
              <img src={mediaUrl(mainImage)} alt="Post preview" />
            )}
            {form.PublicationType === "native-video" && form.Video && (
              <video controls poster={mediaUrl(form.VideoPoster)}>
                <source src={mediaUrl(form.Video)} />
              </video>
            )}
          </div>
          <span className="text-muted fs-8 d-block mt-2">
            Preview crop: {orientationLabel}. Edges may be cropped.
          </span>
        </>
      )}
      {additionalImages.length > 0 && (
        <div className="d-flex flex-wrap gap-2 mt-2">
          {additionalImages.map((url) => (
            <img
              key={url}
              className="img-fluid rounded"
              style={{ width: 120, height: 90, objectFit: "cover" }}
              src={mediaUrl(url)}
              alt="Additional gallery preview"
            />
          ))}
        </div>
      )}
      {form.PublicationType === "youtube" && form.YoutubeVideoId && (
        <div className="text-muted mt-2">YouTube: {form.YoutubeVideoId}</div>
      )}
      {form.PublicationType === "external-link" && form.ExternalUrl && (
        <div className="border rounded p-3 mt-2">
          {form.ExternalImage && (
            <img
              className="img-fluid rounded mb-2"
              src={mediaUrl(form.ExternalImage)}
              alt="Link preview"
            />
          )}
          <a
            className="link-primary d-block"
            href={form.ExternalUrl}
            target="_blank"
            rel="noreferrer"
          >
            {form.ExternalTitle || form.ExternalUrl}
          </a>
          <span className="text-muted">{form.ExternalDescription}</span>
        </div>
      )}
      {form.CtaLabel && form.CtaUrl && (
        <a
          className="btn btn-sm btn-primary mt-3"
          href={form.CtaUrl}
          target="_blank"
          rel="noreferrer"
        >
          {form.CtaLabel}
        </a>
      )}
    </div>
  );
}
function MediaOrientationControl({
  value,
  onChange,
}: {
  value: MediaOrientation;
  onChange: (orientation: MediaOrientation) => void;
}): ReactElement {
  return (
    <div className="col-12">
      <label className="form-label mb-2">Media format</label>
      <div className="btn-group" role="group" aria-label="Media format">
        <button
          type="button"
          className={`btn btn-sm ${value === "portrait" ? "btn-primary" : "btn-light"}`}
          onClick={() => onChange("portrait")}
        >
          Portrait · 9:16
        </button>
        <button
          type="button"
          className={`btn btn-sm ${value === "landscape" ? "btn-primary" : "btn-light"}`}
          onClick={() => onChange("landscape")}
        >
          Landscape · 16:9
        </button>
      </div>
    </div>
  );
}
function ErrorText({ error }: { error?: string }): ReactElement | null {
  return error == null ? null : (
    <div className="invalid-feedback d-block">{error}</div>
  );
}
function TextField({
  label,
  value,
  onChange,
  type = "text",
  error,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  error?: string;
}): ReactElement {
  const displayLabel =
    label === "Video alternative text"
      ? "Accessibility description (optional)"
      : label;
  return (
    <div className="col-md-6">
      <label className="form-label">{displayLabel}</label>
      {label === "Video alternative text" && (
        <p className="text-muted fs-8 mb-2">
          Optional description for accessibility. The post title is used as a
          fallback.
        </p>
      )}
      <input
        className={`form-control ${error != null ? "is-invalid" : ""}`}
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
      <ErrorText error={error} />
    </div>
  );
}
function SelectField({
  label,
  value,
  options,
  onChange,
  error,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  error?: string;
}): ReactElement {
  return (
    <div className="col-md-6">
      <label className="form-label required">{label}</label>
      <select
        className={`form-select ${error != null ? "is-invalid" : ""}`}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      <ErrorText error={error} />
    </div>
  );
}
function Media({
  label,
  description,
  accept,
  value,
  onUpload,
  onRemove,
  uploading,
  multiple = false,
}: {
  label: string;
  description: string;
  accept: string;
  value?: string | string[];
  onUpload: (files: FileList | null) => void;
  onRemove: (url?: string) => void;
  uploading: boolean;
  multiple?: boolean;
}): ReactElement {
  const values = Array.isArray(value) ? value : value == null ? [] : [value];
  const imageField = accept === "image/*";
  const acceptedTypes = imageField
    ? "image/jpeg,image/png,image/webp,image/gif,.gif"
    : accept;
  const displayLabel =
    label === "Main post image (optional)"
      ? "Main post image or GIF (optional)"
      : label === "Post images"
        ? "Post images or GIFs"
        : label === "Video poster"
          ? "Video preview image (optional)"
          : label;
  const displayDescription =
    label === "Video poster"
      ? "Shown before the video starts. If omitted, the video loads without a custom preview image."
      : description;
  return (
    <div className="col-12">
      <label className="form-label">{displayLabel}</label>
      <p className="text-muted fs-8 mb-2">{displayDescription}</p>
      <label
        className={`btn btn-light-primary btn-sm ${uploading ? "disabled" : ""}`}
      >
        <input
          className="d-none"
          type="file"
          accept={acceptedTypes}
          multiple={multiple}
          disabled={uploading}
          onChange={(event) => {
            onUpload(event.target.files);
            event.currentTarget.value = "";
          }}
        />
        {uploading
          ? "Uploading…"
          : values.length > 0 && !multiple
            ? "REPLACE FILE"
            : "UPLOAD FILE"}
      </label>
      {uploading && <span className="text-muted fs-8 ms-2">Uploading…</span>}
      <div className="mt-3 d-flex flex-wrap gap-2">
        {values.map((url, index) => (
          <div
            className="border rounded p-2 d-flex align-items-center gap-2"
            key={url}
          >
            {imageField ? (
              <img
                src={mediaUrl(url)}
                alt="Uploaded preview"
                style={{ width: 44, height: 44, objectFit: "cover" }}
              />
            ) : (
              <span
                className="text-muted fs-8 text-truncate"
                style={{ maxWidth: 130 }}
              >
                {url.split("/").pop()}
              </span>
            )}
            {multiple && <span className="text-muted fs-8">{index + 1}</span>}
            <button
              type="button"
              className="btn btn-icon btn-light-danger btn-sm"
              disabled={uploading}
              aria-label={`Remove ${displayLabel}`}
              onClick={() => onRemove(multiple ? url : undefined)}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
